import {
	APP_PROMOTION_CONTINUE_PARAM,
	APP_PROMOTION_PATH,
	resolveAppPromotionContinue,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import QRCode from 'qrcode';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StoreBadges } from '../components/StoreBadges';
import { useMobileAppPromotion } from '../hooks/useMobileAppPromotion';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * PD-16558 — the storefront's app page, at /app.
 *
 * A welcome text or email lands here when the storefront has an app, with
 * the original sign-in or set-password link in `?continue=`. The page offers
 * this phone's store first (both badges plus a QR code on a computer) and a
 * "Continue in browser" button that opens the original link, so nobody is
 * forced into the app to finish signing up.
 *
 * `continue` is honored only for this site's own sign-in-link and
 * set-password pages (resolveAppPromotionContinue), so the page can never
 * be used to send someone elsewhere. The QR code always encodes plain /app:
 * a one-time token must never end up on a screen someone else can scan.
 *
 * With no app to offer (the portal site links no store, or this is already
 * the app) the page gets out of the way: it follows `continue` straight
 * away, or says the app is not available yet.
 */
export function AppPromotionPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const promotion = useMobileAppPromotion();
	const site = usePortalSiteStore((s) => s.site);
	// The page can render before the storefront's settings arrive; until
	// then "no app" only means "not loaded yet", so nothing is decided.
	const isSiteResolved = usePortalSiteStore((s) => s.isResolved);
	const continuePath = resolveAppPromotionContinue(
		searchParams.get(APP_PROMOTION_CONTINUE_PARAM),
	);
	const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

	const nothingToOffer = isSiteResolved && promotion == null;
	const isComputer = promotion?.platform === 'other';

	useEffect(() => {
		if (nothingToOffer && continuePath != null) {
			void navigate(continuePath, { replace: true });
		}
	}, [nothingToOffer, continuePath, navigate]);

	useEffect(() => {
		if (!isComputer) {
			return;
		}
		let cancelled = false;
		QRCode.toDataURL(`${window.location.origin}${APP_PROMOTION_PATH}`, {
			margin: 1,
			width: 176,
			errorCorrectionLevel: 'M',
		})
			.then((url) => {
				if (!cancelled) {
					setQrDataUrl(url);
				}
			})
			.catch(() => {
				// The badges still work without the code.
				setQrDataUrl(null);
			});
		return (): void => {
			cancelled = true;
		};
	}, [isComputer]);

	const continueInBrowser = (): void => {
		void navigate(continuePath ?? '/', { replace: continuePath != null });
	};

	if (!isSiteResolved || (nothingToOffer && continuePath != null)) {
		return <div className="min-h-dvh bg-brand-page-bg" />;
	}

	const brandName = site?.displayName ?? '';

	return (
		<div className="min-h-dvh flex flex-col bg-brand-page-bg">
			<header className="flex items-center justify-center bg-brand-primary px-4 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
				{site?.logoUrl != null ? (
					<img
						src={site.logoUrl}
						alt={t(brandName)}
						className="h-10 max-w-[240px] object-contain"
					/>
				) : (
					<span className="text-lg font-semibold text-white">
						{t(brandName)}
					</span>
				)}
			</header>

			<main className="flex flex-1 items-start justify-center px-4 py-8 md:items-center">
				<div className="w-full max-w-md space-y-6 rounded-xl border border-brand-border bg-brand-surface p-6 text-center">
					{promotion == null ? (
						<>
							<h1 className="text-2xl font-semibold text-default">
								{t('The {{name}} app is not available yet', {
									name: t(brandName),
								})}
							</h1>
							<p className="text-sm text-brand-muted">
								{t('You can do everything on this website in the meantime.')}
							</p>
						</>
					) : (
						<>
							{promotion.app.appIconUrl != null && (
								<img
									src={promotion.app.appIconUrl}
									alt=""
									className="mx-auto h-20 w-20 rounded-2xl shadow-md"
								/>
							)}
							<div className="space-y-2">
								<h1 className="text-2xl font-semibold text-default">
									{t('Get the {{name}} app', { name: t(brandName) })}
								</h1>
								<p className="text-sm text-brand-muted">
									{t(
										'Order, reorder and pay from your phone, and sign in with a tap.',
									)}
								</p>
							</div>
							<StoreBadges
								app={promotion.app}
								platform={promotion.platform}
								className="justify-center"
							/>
							{isComputer && qrDataUrl != null && (
								<div className="space-y-2">
									<img
										src={qrDataUrl}
										alt={t('QR code for the {{name}} app page', {
											name: t(brandName),
										})}
										width={176}
										height={176}
										className="mx-auto rounded-lg bg-white p-2"
									/>
									<p className="text-xs text-brand-muted">
										{t("Scan with your phone's camera to get the app.")}
									</p>
								</div>
							)}
						</>
					)}

					<div className="border-t border-brand-border pt-4">
						<button
							type="button"
							onClick={continueInBrowser}
							className="w-full rounded-md border border-brand-border px-4 py-2.5 text-sm font-medium text-default hover:bg-brand-input"
						>
							{continuePath != null
								? t('Continue in browser')
								: t('Go to the {{name}} website', { name: t(brandName) })}
						</button>
					</div>
				</div>
			</main>
		</div>
	);
}
